import { Alert, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { memo, useCallback, useMemo, useRef } from "react";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import NoneFoundReport from "src/components/none-found-report";
import { TruxTableCell } from "src/components/trux-table-cell";
import { enqueueSnackbar } from "notistack";
import { EldVehicle } from "src/models";
import { DownloadExcelButton, ReportRunner, useDateRangeFilter, useVehicleFilter } from "../runner/report-runner";

type HarshBreakingLine = {
	vehicleUnitNumber: string,
	driverName: string,
	date: string,
	time: string,
	location: string,
	occurredAt: string,
}

type HarshBreakingSummaryLine = {
	vehicleUnitNumber: string,
	date: string,
	occurrences: number,
	occurredAt: string,
}

type HarshBreakingReportSummariesProps = {
  summaries: HarshBreakingSummaryLine[],
}

const HarshBreakingReportSummaries = memo(({ summaries }: HarshBreakingReportSummariesProps) => {
  const { sortedList, sortData } = useSort(summaries, "occurredAt", "desc");

  if (!sortedList) {
    return null;
  }

  return <div>
    <Typography variant="h6" sx={{ mb: 1 }}>
      Occurrences By Truck
    </Typography>

    <Card sx={{ mb: 3 }}>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="vehicleUnitNumber">
                Truck
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="occurredAt">
                Date
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="occurrences" align="center">
                Occurrences
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((summary, index) =>
              (<TableRow key={index}>
                <TruxTableCell>
                  {summary.vehicleUnitNumber}
                </TruxTableCell>
                <TruxTableCell>
                  {summary.date}
                </TruxTableCell>
                <TruxTableCell align="center">
                  {summary.occurrences}
                </TruxTableCell>
              </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type HarshBreakingReportLinesProps = {
  lines: HarshBreakingLine[],
}

const HarshBreakingReportLines = memo(({ lines }: HarshBreakingReportLinesProps) => {
  const { sortedList, sortData } = useSort(lines, "occurredAt", "desc");

  if (!sortedList) {
    return null;
  }

  return <div>
    <Typography variant="h6" sx={{ mb: 1 }}>
      Details
    </Typography>

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="vehicleUnitNumber">
                Truck
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="driverName">
                Driver
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="occurredAt">
                Date
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="occurredAt">
                Time
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="location">
                Location
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((line, index) =>
              (<TableRow key={index}>
                <TruxTableCell>
                  {line.vehicleUnitNumber}
                </TruxTableCell>
                <TruxTableCell>
                  {line.driverName}
                </TruxTableCell>
                <TruxTableCell>
                  {line.date}
                </TruxTableCell>
                <TruxTableCell>
                  {line.time}
                </TruxTableCell>
                <TruxTableCell>
                  {line.location}
                </TruxTableCell>
              </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  eldVehicles?: EldVehicle[],
  lines?: HarshBreakingLine[],
  summaries?: HarshBreakingSummaryLine[],
}

export default function Page() {
  const settings = useSettingsContext();

  const currentRequest = useRef({} as any);

  const dataLoader = useCallback(() => {
    const report = (currentRequest.current as any).ran
      ? axios.post(endpoints.reports.harshBreaking.data, JSON.stringify(currentRequest.current))
      : undefined;

    return {
      eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
      lines: report && report.then(response => response.data.lines),
      summaries: report && report.then(response => response.data.summaries),
    };
  }, []);
  const {
    data,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  const vehicles = useMemo(() => (data?.eldVehicles || [])
    .filter((vehicle) => vehicle.active)
    .sort((a, b) => a.vehicleUnitNumber.localeCompare(b.vehicleUnitNumber)),
  [data?.eldVehicles]);

  const dateRangeFilter = useDateRangeFilter();
  const vehicleFilter = useVehicleFilter(vehicles);

  const filters = [
    dateRangeFilter,
    vehicleFilter,
  ];


  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <Alert severity="info">
       The report is only showing harsh breaking events detected after the 24th of Nov 2025.
      </Alert>

      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Harsh Breaking">
          <DownloadExcelButton baseUrl={endpoints.reports.harshBreaking.excel} filters={filters} currentRequest={currentRequest} />
        </PageHeader>

        <ReportRunner
          filters={filters}
          onValidate={(errorMessage) => enqueueSnackbar(errorMessage, {variant: "error"})}
          onRun={(request) => {
            currentRequest.current = request;
            setShouldRefresh(true);
          }}
          loading={loading}
        />

        {data && (data.summaries?.length || 0) > 0 && <HarshBreakingReportSummaries summaries={data.summaries || []} />}
        {data && (data.lines?.length || 0) > 0 && <HarshBreakingReportLines lines={data.lines || []} />}
        {loaded && data && data.lines?.length === 0 && <NoneFoundReport />}
      </DisplayPageData>
    </Container >
  );
}
